import chalk from "chalk";
import { IPty, spawn } from "node-pty";
import path from "path";

import { pubSubServer } from "..";

import { SocketTerminal } from "./terminal";

export class ForkedProcess {
  private ptyProcess: IPty | null = null;

  private start(
    name: string,
    cwd: string,
    filename: string,
    execArgv: string[],
    env: NodeJS.ProcessEnv,
    tags: string[]
  ) {
    if (this.ptyProcess != null) {
      console.error("Tried starting a process already running");
      return;
    }
    const nodeExec = process.env.npm_node_execpath || process.execPath;
    const args = [...execArgv, path.relative(cwd, filename)];
    this.ptyProcess = spawn(nodeExec, args, {
      name,
      cols: 110,
      rows: 75,
      cwd,
      env: env as any
    });

    const terminal = SocketTerminal.getOrCreateTerminal(name, tags, this.io);
    if (terminal == null) {
      throw new Error("Terminal for '" + this.name + "' is unexpectedly null or undefined");
    }

    terminal.write(`\n\rStarting ${chalk.green(this.name)}`);
    terminal.write(`\n\r - cwd: ${chalk.cyan(cwd)}`);
    terminal.write(`\n\r - filename: ${chalk.cyan(filename)}`);
    terminal.write(`\n\r`);
    let argsStr = args.join(" ");
    argsStr = argsStr.replace(/\s-(?=[^-])/g, "\n\r  -");
    argsStr = argsStr.replace(/\s--(?=[^-])/g, "\n\r  --");
    terminal.write(`\n\r> ${chalk.yellow(nodeExec)}\n\r  ${argsStr}`);
    terminal.write(`\n\r\n\r`);

    terminal.onData = data => {
      if (this.ptyProcess != null) {
        this.ptyProcess.write(data);
      } else {
        if (data === "r") {
          terminal.write(chalk.green.white("\n\rRestarting...\n\r"));
          this.start(name, cwd, filename, execArgv, env, tags);
        }
      }
    };
    this.ptyProcess.on("data", data => {
      terminal.write(data);
    });

    this.ptyProcess.on("exit", code => {
      this.ptyProcess = null;

      terminal.write("\n\rStopped with " + chalk.green(code.toString()));
      console.log("%s exited with %s", name, code);
      if (!this.isKilled && (code >= 10000 || code === 134)) {
        console.log("Restarting %s", name);
        terminal.write(chalk.green.white("\n\rRestarting...\n\r"));
        this.start(name, cwd, filename, execArgv, env, tags);
      }
    });
  }

  constructor(
    readonly name: string,
    readonly cwd: string,
    readonly filename: string,
    readonly execArgv: string[],
    env: NodeJS.ProcessEnv,
    readonly tags: string[] = [],
    private readonly io: SocketIO.Server
  ) {
    console.log("Forking %s", name);

    const processEnv = {
      ...process.env,
      ...env,
      NODE_ENV: "development",
      SERVER_ID: pubSubServer().id
    };
    Object.keys(processEnv).forEach(key =>
      processEnv[key] === undefined ? delete processEnv[key] : ""
    );

    this.start(name, cwd, filename, execArgv, processEnv, tags);
  }

  private isKilled: boolean = false;

  kill() {
    const terminal = SocketTerminal.getTerminal(this.name);
    if (terminal == null) {
      throw new Error("Terminal for '" + this.name + "' is unexpectedly null or undefined");
    }

    terminal.write(`\n\rStopping ${chalk.cyan(this.name)}`);
    terminal.write(`\n\r`);

    console.log("Killing %s", this.name);
    if (this.ptyProcess == null) {
      return;
    }
    this.isKilled = true;
    this.ptyProcess.kill();
    this.ptyProcess = null;
  }

  write(msg: string) {
    const terminal = SocketTerminal.getTerminal(this.name);
    if (terminal == null) {
      throw new Error("Terminal for '" + this.name + "' is unexpectedly null or undefined");
    }

    terminal.write(msg);
  }
}
